<template>
    <div>
        <!-- dialog price -->
        <v-dialog
			v-model="dialogPriceType"
			persistent
			width="400"
			@keydown.esc="dialogPriceType = false"
        >
            <v-card>
                <div class="d-flex py-1 primary">
                    <v-card-title
                        class="text-uppercase flex-grow-1 text-subtitle-1 justify-center pa-0 white--text"
                    >
                        {{ $t('add-prices-for-an-item') }}
                    </v-card-title>

                    <v-btn icon @click="dialogPriceType = false">
                        <v-icon color="white"> mdi-close-circle-outline </v-icon>
                    </v-btn>
                </div>

                <v-card-text>
                    <v-row class="mt-3">
                        <p class="mb-2">
                            {{ $t('please-enter-the-type-price-for-this-item', { type: pricesTypes.find(c => c.id === tableInputs.priceType)?.name }) }}
                        </p>
                        <v-col 
                            sm="6" 
                            cols="12" 
                            class="pa-1"
                        >
                            <v-text-field
                                v-model.number="price"
                                clearable
                                outlined
                                dense
                                hide-details="auto"
                                :label="$t('inputs.price')"
                                :loading="submitLoading"
                                :disabled="submitLoading"
                                :rules="rules.requiredAsNumber"
                                autofocus
                            />
                        </v-col>

                        <v-col 
                            sm="6" 
                            cols="12" 
                            class="pa-1"
                        >
                            <v-text-field 
                                v-model.number="priceDiscountRate"
                                clearable
                                outlined
                                dense
                                hide-details="auto"
                                :label="$t('inputs.discount-rate')"
                                :disabled="submitLoading"
                                :rules="rules.requiredAsNumber"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions class="justify-end pt-0">
                    <v-btn
                        color="success"
                        text
                        :loading="submitLoading"
                        @click="updateItemPrice()"
                    >
                        {{$t('save')}}
                    </v-btn>
                    <v-btn
                        @click="dialogPriceType = false"
                        text
                        :disabled="submitLoading"
                    >
                        {{$t('cancel')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <!-- dialog edit transaction -->
        <v-dialog v-model="dialog" persistent width="400" @keydown.esc="dialog = false">
            <v-card>
                <div class="d-flex primary">
                    <v-card-title
                        class="text-uppercase flex-grow-1 text-subtitle-1 d-flex justify-center pa-0 white--text"
                    >
                        {{ $t('JournalItems.modify-transfer') }}
                    </v-card-title>
                    <!-- close button -->
                    <v-btn icon @click="dialog = false">
                        <v-icon color="white">
                            mdi-close-circle-outline
                        </v-icon>
                    </v-btn>
                </div>
                <v-card-text class="mt-3 pb-1">
                    <v-row class="mt-1">
                        <v-col cols="6" class="px-1 pt-1">
                            <v-autocomplete
                                v-model="accountId"
                                :items="allAccountsBrief"
                                item-value="accountId"
                                item-text="name"
                                outlined
                                dense
                                hide-details
                                :label="$t('inputs.account')"
                                autofocus
                            />
                        </v-col>

                        <v-col cols="6" class="px-1 pt-1">
                            <v-text-field
                                v-model.number="quantityIn"
                                :label="$t('inputs.input-quantity')" 
                                type="number" 
                                outlined
                                dense
                                hide-details="auto"
                                :rules="!quantityOut && !quantityIn ? rules.required : []"
                            />
                        </v-col>

                        <v-col cols="6" class="px-1 pt-1">
                            <v-text-field 
                                v-model.number="quantityOut"
                                :label="$t('inputs.out-quantity')" 
                                type="number" 
                                outlined
                                hide-details="auto"
                                dense
                                :rules="!quantityIn && !quantityOut ? rules.required : []"
                            />
                        </v-col>

                        <v-col cols="6" class="px-1 pt-1">
                            <v-autocomplete
                                v-model="materialId"
                                :items="materialBrief"
                                item-value="id"
                                :item-text="(item) => `${item.name} - ${item.remaining}`"
                                :disabled="loading"
                                :label="$t('inputs.item')"
                                outlined
                                dense
                                hide-details
                            />
                        </v-col>

                        <v-col v-if="addPrice" cols="6" class="py-1">
							<v-text-field
								v-model.number="sellPrice"
								:label="$t('inputs.price')"
								outlined
								dense
								hide-details
								:disabled="submitLoading"
							/>
						</v-col>

                        <v-col cols="6" class="px-1 pt-1">
                            <v-text-field 
                                v-model="notes"
                                :label="$t('inputs.notes')"   
                                outlined
                                dense
                                hide-details
                            />
                        </v-col>
    
                        <v-col cols="12" class="px-1 pt-1">
                            <v-autocomplete 
                                v-model="state"
                                :items="transactionState"
                                item-text="name"
                                item-value="id"
                                :label="$t('JournalItems.reason')"
                                dense
                                outlined
                                hide-details
                                :disabled="Boolean(quantityIn)"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn color="success" small @click="edit()" :loading="submitLoading">
                        {{$t('save')}}
                    </v-btn>
                    <v-btn color="alert-color white--text" small :disabled="submitLoading" @click="dialog = false">
                        {{$t('cancel')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <div class="d-flex flex-wrap justify-sm-space-between justify-end align-center">
            <div class="flex-sm-grow-0 flex-grow-1 order-sm-0 order-1">
                <v-tabs
                	v-model="tab"
                	height="40"
                	grow
                	@change="addTransactionToEntity = false; $nextTick(() => { $refs.table.resetInputs() }); updateTableDueTab()"
                >
                    <v-tab
                        v-for="(item, index) in types"
                        :key="index"
                        :disabled="loading"
                        class="text-capitalize"
                    >
                        {{ item.name }}
                    </v-tab>
                    <v-tabs-slider color="primary"></v-tabs-slider>
                </v-tabs>
                <v-divider class="mx-1"/>
            </div>
            <v-btn
                v-if="isSuperAdmin || permissionsUser.MaterialTransactions !== roleName.nothing"
                small
                :to="{ name: 'supervisor-remaining-samples' }"
                color="light-blue white--text"
            >
                {{$t('JournalItems.remaining-samples')}}
            </v-btn>
        </div>

        <!-- filter -->
		<v-row class="my-2" dense>
            <v-col v-if="addFilterAccountEmployee" cols="12" sm="4" md="3">
                <v-autocomplete
                    v-model="accountIdFilter"
                    :items="staff"
                    item-text="name"
                    item-value="accountId"
                    dense
                    outlined
                    hide-details
                    label="Employee"
                    :disabled="loading"
                    @change="needToResetShownRows = true; getJournal()"
                    clearable
                />
            </v-col>
			<v-col cols="12" sm="6" md="3">
				<bee-date-input
					v-model="date"
					:label="$t('inputs.date')"
					input-format-order="DD-MM-YYYY"
					dense
                    :disabled="loading"
					:append-icon="$i18n.locale === 'en' ? 'mdi-menu-right' : 'mdi-menu-left' "
					:prepend-icon="$i18n.locale === 'en' ? 'mdi-menu-left' : 'mdi-menu-right'"
                    hide-details
					@click:append="
                        needToResetShownRows = true;
                        addDate();
                        getJournal();
                    "
					@click:prepend="
                        needToResetShownRows = true;
                        subDate();
                        getJournal();
                    "
				></bee-date-input>
			</v-col>
		</v-row>
        <bee-handy-smart-table
            v-model="tableInputs"
            ref="table"
            :items="[]"
            :headers="headers"
            :items-per-page="50"
            :loading="loading || submitLoading"
            :show-add-row="permissionsUser.GenerateMaterials !== roleName.read && (!loading) && !submitLoading"
            dense
            zebra
            hot-inputs
            hide-inputs-details
            :row-highlight-expression="item => item.type === 0"
			row-highlight-expression-class="orange lighten-4"
            fixed-header
            :height="$vuetify.breakpoint.height - ($vuetify.breakpoint.smAndUp ? 190 : 220)"
            :scroll-options.sync="scrollOptions"
            pagination-on-scroll
            pagination-on-scroll-server-items
            :server-items-length="serverItemsLength"
            :disable-input-fields="submitLoading || loading || (tab === 1 && addTransactionToEntity === false)"
            @keypress.enter="loading === false ? addItem() : null"
            @sheet-add-clicked="addItem"
            @pagination-on-scroll:reach-last-row="getJournal"
            :shown-rows-text="$t('shown-rows')"
            @change-freeSample="resetInputsPrices"
            @change-materialId="changeOnChangeMaterial"
            @change-priceType="getPriceValue"
            @change-priceDiscount="updatePrice"
            @change-sellPrice="updateX"
            @input-sellPrice="tableInputs.priceDiscount = 0 ;customPrice = true"
        >   
            <!-- payment methods 'mirta' -->
            <template v-slot:input.payment="{ on, attr }">
                <v-autocomplete 
                    :items="paymentMethods"
                    item-text="name"
                    item-value="id"
                    dense
                    v-on="on"   
                    v-bind="attr"
                    :disabled="tableInputs.freeSample"
                />
            </template>
            <!--accounts -->
            <template v-slot:input.accountId="{ on ,attr }">
                <v-autocomplete
                    :items="tab === 0 ? staff : customers"
                    item-value="accountId"
                    :item-text="(item) => (includeAddressWithCustomers && tab !== 0) ? `${item.name} - ${item.address ? item.address : '-'}` : `${item.name}`"
                    v-on="on"   
                    v-bind="attr"
                />
            </template>

            <!-- materialId -->
            <template v-slot:input.materialId="{ on ,attr }">
                <v-autocomplete
                    :items="materialBrief"
                    item-value="id"
                    :item-text="(item) => `${item.name} - ${item.remaining}`"
                    v-on="on"
                    v-bind="attr"
                />
            </template>

            <!-- date -->
      		<template v-slot:input.date="{ on, attr }">
                <v-menu
					ref="dateInputTable"
					v-model="dateInputTable"
					:close-on-content-click="false"
					transition="scale-transition"
					offset-y
					min-width="auto"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							v-model="tableInputs.date"
							clearable
							type="date"
                            v-on="on"
                            v-bind="attr"
						>
							<template v-slot:prepend>
								<v-icon
									v-bind="attrs"
									v-on="on"
								>
									mdi-calendar
								</v-icon>
							</template>
						</v-text-field>
					</template>
					<v-date-picker
						v-model="tableInputs.date"
						:active-picker.sync="activePickerDate"
						:max="(new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)"
						min="1940-01-01"
						:allowed-dates="disableDates"
						@change="saveDate"
					></v-date-picker>
				</v-menu>
			</template>

            <template v-slot:input.priceType="{ on, attr }">
                <v-autocomplete 
                    :items="pricesTypes"
                    item-value="id"
                    item-text="name"
                    dense
                    hide-details
                    v-on="on"
                    v-bind="attr"
                />
            </template>

            <template v-slot:item.description="{ item }">
				<v-icon 
					v-if="item.notes && item.notes.includes('Transaction from visit')" 
					color="success"
				> 
					mdi-check-circle 
				</v-icon>
				<v-icon 
					v-else
					color="alert-color"
				> 
					mdi-close-circle 
				</v-icon>
			</template>

            <template v-slot:input.state="{ on ,attr }">
                <v-autocomplete
                    :items="transactionState"
                    item-text="name"
                    item-value="id"
                    dense
                    v-on="on"
                    v-bind="attr"
                    label=" "
                />
            </template>

            <template v-slot:input.freeSample="{ on, attr }">
                <v-checkbox 
                    v-model="tableInputs.freeSample"
                    dense
                    hide-details
                    v-on="on"   
                    v-bind="attr"
                />
            </template>

            <template v-slot:item.freeSample="{ item }">
                <v-chip small>
                    <span :class="item.freeSample === true ? 'primary--text' : 'paid-color-text'">
                        {{ item.freeSample ? 'Free' : 'Paid' }}
                    </span>
                </v-chip>
            </template>

            <template v-slot:input.priceDiscount="{ on, attr }">
                <v-text-field 
                    dense
                    hide-details
                    v-on="on"
                    v-bind="attr"
                    prefix="%"
                    :disabled="disabledDiscount || tableInputs.freeSample"
                />
            </template>

            <template v-slot:no-input.actions>
                <!-- <tooltip text="Add transaction" right>   -->
                    <v-btn
                    	color="success"
                    	small
                    	icon
                    	:disabled="loading"
                    	@click="addItem"
                    >
                        <v-icon>mdi-check-circle-outline</v-icon>
                    </v-btn>
                <!-- </tooltip> -->
            </template>

            <template v-slot:item.actions="{ on, attr, item }">
                <!-- <tooltip text="Add transaction" right>   -->
                    <v-btn
                        v-if="0"
                        color="success"
                        small
                        icon
                        @click="dialog = true; dataRow = item; isEdit = true"
                        v-on="on"
                        v-bind="attr"   
                    >
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                <!-- </tooltip> -->
            </template>
        </bee-handy-smart-table>
    </div>
</template>

<script>
import rules from '@/validation rules'
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { roleName, transactionState, paymentMethods } from '@/helpers/enums'
import { addFilterAccountEmployee, addPrice, enableFreeDistributionWithPrice, includeAddressWithCustomers, enablePricesTypes, enableDiscount, isIntNumberRule, isDisabledFriday, enableVacations } from '@/configs/global.config'

import { disableDates } from '@/helpers/functions'
import pricesTypes from '@/store/modules/pricesTypes'
export default {
    name: 'JournalItems',

    watch: {
        dialog (val) {
            if (val) {
                this.accountId = this.dataRow.accountId
                this.materialId = this.dataRow.materialId
                this.quantityIn = this.dataRow.in
                this.quantityOut = this.dataRow.out  
                this.notes = this.dataRow.notes
                if (this.addPrice) this.sellPrice = this.dataRow.sellPrice
            }
            if (val === false) {
                this.isEdit = false
            }
        },
        
        quantityIn (val) {
            if (val !== null) {
                this.quantityOut = null
            }
        },

        quantityOut (val) {
            if (val !== null) {
                this.quantityIn = null
            }
        },

        dateInputTable (val) {
            val && setTimeout(() => (this.activePickerDate))
        },

        dialogPriceType (val) {
            if (!val) {
                this.price = 0
                this.priceDiscountRate = 0
            }
        }
    },
   
    data() {
        return {
            dialog: false,
            roleName,
            tab: 0,
            loading: true,
            submitLoading: false,
            tableInputs: {
                freeSample: false,
                sellPrice: 0,
            },
            rules,  
            date: moment().format('YYYY-MM-DD'),

            // briefAccounts: [
            //     [], // for staff
            //     [], // for customers
            //     [], // for external
            // ],
            
            // pagination
            scrollOptions: {},
            serverItemsLength: 0,
            needToResetShownRows: false,
            transactionState,
            // dialog edit transaction
            accountId: null,
            materialId: null,
            quantityIn: null,
            quantityOut: null,
            sellPrice: 0,
            notes: null,
            dataRow: null,
            state: 0, 
            addTransactionToEntity: false,
            accountIdFilter: null,
            addFilterAccountEmployee,
            addPrice,
            paymentMethods,
            enableFreeDistributionWithPrice,
            includeAddressWithCustomers,
            enablePricesTypes,
            enableDiscount,
            materialsWithPrices: [],
            disabledDiscount: false,
            // allMaterials: [],
            customPrice: false,
            isDisabledFriday, 
            enableVacations,
            testDate: null,
            showPicker: false,
            selectedDate: null,
            disableDates,
            activePickerDate: null,
            dateInputTable: false,
            dialogPriceType: false,
            priceDiscountRate: 0,
            price: 0
        }   
    },

    computed: {
        ...mapState({
            // materials: state => state.materials.materials,
            permissionsUser: state => state.auth.youPermissions,
            isSuperAdmin: state => state.auth.isSuperAdmin,
            // myMaterials: state => state.materials.myMaterials,
            pricesTypes: state => state.pricesTypes.pricesTypes,
            employeeId: state => state.auth.self.employeeId,
            vacationsDayDetails: state => state.vacations.vacationsDayDetails,
            materialBrief: state => state.materials.materialBrief,
            staff: state => state.accounts.staff,
            customers: state => state.accounts.customers
        }),

        ...mapGetters({
            getMaterialById: 'materials/getMaterialById'
        }),

        headers() {
            const headers = [
                {
                    text: this.$t('headers.input-quantity'),
                    name: 'in',
                    value: 'in',
                    setInputExpr: (inputs, activeInput) => {
						if (activeInput === 'out' && inputs.out) {
							return null
						}
						return inputs.in
					},  
                    // integer for ibn-alhaytham and oubari or for all pharmacy company
                    inputRules:
                        isIntNumberRule 
                        ? !this.tableInputs.out ? rules.IntNumber : [] 
                        : !this.tableInputs.out && !this.tableInputs.in ? rules.requiredAsNumber : [],
                    // inputRules: !this.tableInputs.out ? isIntNumberRule ? rules.IntNumber : rules.required : [],
                    type: 'number',
                    width: 'auto',
                    sortable: false
                },
                {
                    text: this.$t('headers.output-quantity'),
                    name: 'out',
                    value: 'out',
                    setInputExpr: (inputs, activeInput) => {
						if (activeInput === 'in' && inputs.in) {
							return null
						}
						return inputs.out
					},  
                    inputRules:
                        isIntNumberRule
                        ? !this.tableInputs.in ? rules.IntNumber : [] 
                        : !this.tableInputs.in && !this.tableInputs.out === null ? rules.requiredAsNumber : [],
                    type: 'number',
                    width: 'auto',
                    class: 'text-center',
                    cellClass: 'text-center',
                    columnClass: 'text-center',
                    inputClass: 'text-center',
                    sortable: false
                },
                {
                    text: this.$t('headers.item'),
                    name: 'materialId',
                    value: 'materialId',
                    setValueExpr: val => this.materialBrief.find(c => c.id === val)?.name,
                    sortable: false
                },
                enableFreeDistributionWithPrice && addPrice && this.tab !== 0 ? {
                    text: this.$t('headers.free'),
                    name: 'freeSample',
                    value: 'freeSample',
                    type: 'Boolean',
                    sortable: false
                } : null,
                // for prices types and price for this type and discount
                addPrice && enablePricesTypes && this.tab !== 0 ? {
                    text: this.$t('headers.price-type'),
                    name: 'priceType',
                    value: 'priceType',
                    sortable: false,
                    disableInput: this.tableInputs.freeSample || 
					!this.tableInputs.materialId ||
					(this.tableInputs.materialId && this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices.length === 0)
                }
                : null,
                addPrice && this.tab !== 0 ? {
                    text: this.$t('headers.sell-price'),
                    name: 'sellPrice',
                    value: 'sellPrice',
                    inputRules: this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices.length && 
                            this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices.find(c => c.priceId === this.tableInputs.priceType)?.price !== 0 && 
                            this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices.find(c => c.priceId === this.tableInputs.priceType)?.priceDiscount > 0 ? [val => {
                        const material = this.materialBrief.find(c => c.id === this.tableInputs.materialId)
                        const priceForPriceType = material.prices.find(c => c.priceId === this.tableInputs.priceType)?.price
                        const discount = material?.prices.find(c => c.priceId === this.tableInputs.priceType)?.priceDiscount / 100 
                        return (priceForPriceType - (priceForPriceType * discount)) <= Number(val) && priceForPriceType >= Number(val)
                    }] : [],
                    type: 'number',
                    disableInput: this.tableInputs.freeSample || !this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices.find(c => c.priceId === this.tableInputs.priceType) || this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices.find(c => c.priceId === this.tableInputs.priceType).price === 0,
                    sortable: false
                } : null,

                addPrice && enableDiscount && this.tab !== 0 
                ? {
                    text: this.$t('headers.discount'),
                    name: 'priceDiscount',
                    value: 'priceDiscount',
                    disableInput: this.disabledDiscount,
                    // inputRules: this.materialBrief.find(c => c.id === this.tableInputs.materialId) && this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices.length !== 0 && this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices.find(c => c.priceId === this.tableInputs.priceType)?.priceDiscount > 0 ? [val => 
                    //     val <= this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices.find(c => c.priceId !== this.tableInputs.priceType).priceDiscount
                    // ] : false,
                    inputRules: [val => val <= (this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices.find(c => c.priceId === this.tableInputs.priceType)?.priceDiscount) || val === 0],
                    sortable: false,
                    type: 'number'
                } 
                : null, 

                addPrice && this.tab !== 0 ? {
                    text: this.$t('headers.payment-method'),
                    name: 'payment',
                    value: 'payment',
                    setValueExpr: val => val ? this.paymentMethods.find(c => c.id === val)?.name : '-',
                    sortable: false
                } : null,
                {
                    text: this.$t('headers.notes'),
                    name: 'notes',
                    value: 'notes',
                    setValueExpr: val => val ? val.includes('Transaction from visit') ? '- ' : val : '-',
                    sortable: false
                },
                
                {
                    text: this.$t('headers.date'),
                    name: 'date',
                    // type: 'date',
                    value: 'date',
                    // dateFormat: 'DD-MM-YYYY',
                    setValueExpr: val => moment(val).format('YYYY-MM-DD'),
                    width: 212,
                    sortable: false
                },
                {
                    text: '',
                    name: 'actions',
                    value: 'actions',
                    width: 60,
                    noInput: true,
                    noNotAvailableText: true,
                    sortable: false
                }
            ]

            if (this.tableInputs.out !== null && this.tab === 0) {
                headers.splice(4, 0, {
                    text: this.$t('headers.state'),
                    name: 'state',
                    value: 'state',
                    sortable: false
                });
            }
            if (this.tab !== 1) {
                headers.unshift({
                    text: this.$t('headers.account'),
                    name: 'accountId',
                    value: 'accountId',
                    setValueExpr: val => this.tab === 0 ? this.staff.find(c => c.accountId === val)?.name : this.customers.find(c => c.accountId === val)?.name,
                    sortable: false
                })
            } else {
                headers.unshift({
                    text: this.$t('headers.account'),
                    name: 'accountId',
                    value: 'accountName',
                    sortable: false
                })
            }

            if (this.tab !== 0) {
                if (this.addPrice) {
					headers.splice(4, 0, 
                    // for space between column
                    !addPrice && !enableDiscount ? {
                        text: this.$t('headers.transaction-from-visit'),
                        name: 'description',
                        value: 'description',
                        setValueExpr: val => val ? val.includes('Transaction from visit') : ' ',
                        type: 'string',
                        noInput: true,
                        sortable: false
                    } : null)
				} else {
                    headers.splice(5, 0, {
                        text: this.$t('headers.transaction-from-visit'),
                        name: 'description',
                        value: 'description',
                        setValueExpr: val => val ? val.includes('Transaction from visit') : ' ',
                        type: 'string',
                        noInput: true,
                        sortable: false
                    })
                }
            }
            
            return headers.filter(c => Boolean(c))
            // if (this.tab === 0) {
            //     headers = [
            //         {
            //             text: this.$t('headers.account'),
            //             name: 'accountId',
            //             value: 'accountId',
            //             setValueExpr: val => this.allAccountsBrief.find(c => c.accountId === val)?.name,
            //         },
            //         {
            //             text: this.$t('headers.input-quantity'),
            //             name: 'in',
            //             value: 'in',
            //             setInputExpr: (inputs, activeInput) => {
            //                 if (activeInput === 'out' && inputs.out) {
            //                     return null
            //                 }
            //                 return inputs.in
            //             },  
            //             inputRules: !this.tableInputs.in && !this.tableInputs.out === null ? rules.required : [],
            //             type: 'number'     
            //         },
            //         {
            //             text: this.$t('headers.output-quantity'),
            //             name: 'out',
            //             value: 'out',
            //             setInputExpr: (inputs, activeInput) => {
            //                 if (activeInput === 'in' && inputs.in) {
            //                     return null
            //                 }
            //                 return inputs.out
            //             },  
            //             inputRules: !this.tableInputs.out && !this.tableInputs.in ? rules.required : [],
            //             type: 'number'     
            //         },
            //         {
            //             text: this.$t('headers.item'),
            //             name: 'materialId',
            //             value: 'materialId',
            //             setValueExpr: val => this.myMaterials.find(c => c.id === val)?.name,
            //         },
            //         {
            //             text: this.$t('headers.notes'),
            //             name: 'notes',
            //             value: 'notes'
            //         },
            //         {
            //             text: this.$t('headers.date'),
            //             name: 'date',
            //             type: 'date',
            //             value: 'date',
            //             dateFormat: 'DD-MM-YYYY',
            //             width: 180
            //         }
            //     ]
            //     if (this.tableInputs.out !== null) {
            //         headers.push({
            //             text: this.$t('headers.state'),
            //             name: 'state',
            //             value: 'state'
            //         },
            //         {
            //             text: '',
            //             name: 'actions',
            //             value: 'actions',
            //             width: 60,
            //             noInput: true,
            //             noNotAvailableText: true,
            //             sortable: false
            //         }
            //     )
            //     } else {
            //         headers.push({
            //             text: '',
            //             name: 'actions',
            //             value: 'actions',
            //             width: 60,
            //             noInput: true,
            //             noNotAvailableText: true,
            //             sortable: false
            //         })
            //     }
            // } 
        },
        currentUserData() {
            return this.tab === 0 && this.staff.length
                ? this.staff.find(c => c.id === this.employeeId) || {}
                : {};
        },

        allAccountsBrief() {
            return this.tab === 0 ? this.staff : this.customers;
        },

        types() {
            return [
                { id: 0, name: this.$t('tabs.staff') },
                { id: 1, name: this.$t('tabs.customers') },
                { id: 2, name: this.$t('tabs.external') }
            ]
        }
    },

    methods: {
        updateTableDueTab() {
            this.$refs.table.resetShownRows();
            this.$refs.table.resetInputs();
            this.$router.replace({ name: 'journal-items', query: { tab: this.tab } });

            this.tableInputs.accountId = this.currentUserData.accountId;

            this.updateJournal();
        },

        async updateJournal(isFirstLoad = false) {
            this.loading = true;
            // if (this.tab !== 1) {
             this.addTransactionToEntity = true
                return Promise.all([
                    // await this.getAllAccount(),
                    this.getJournal(null, true),
                ]).finally(() => {
                    this.loading = false;
                })
            // } else {
			    // this.needToResetShownRows = true; 
                // this.getJournal(null, true).finally(() => {
                //     this.loading = false
                // })
                // this.loading = true
                // this.$store.dispatch('accounts/fetchAllBrief', {
                //     regionIds: [],
                //     type: this.tab,
                // }).then((data) => {
                //     this.briefAccounts[this.tab].push(...data);
                //     this.addTransactionToEntity = true
                // })
            // }
        },

        addItem() {
            if (this.loading) return;
            if (this.$refs.table.validate()) {
                this.submitLoading = true;
                this.$store.dispatch('materialTransactions/singleTransaction', { 
                    materialId: this.tableInputs.materialId, 
                    accountId: this.tableInputs.accountId, 
                    out: this.tableInputs.out ? this.tableInputs.out : null, 
                    inAmount: this.tableInputs.in ? this.tableInputs.in : null, 
                    notes: this.tableInputs.notes,
                    state: this.state,
                    date: this.tableInputs.date !== null ? this.tableInputs.date : moment().format('DD-MM-YYYY'),
                    sellPrice: this.tableInputs.sellPrice,
                    payment: this.tableInputs.payment ? this.tableInputs.payment : null,
                    freeSample: this.tableInputs.freeSample,
                    priceDiscount: +(this.tableInputs.priceDiscount)
                }).then(() => {
                    this.needToResetShownRows = true;
                    this.getJournal(null, false);
                }).finally(() => {
                    this.submitLoading = false;
                    this.tableInputs.in = null;
                    this.tableInputs.out = null;
                    this.$refs.table.resetInputsFocus();
                })
            }
        },

        // getAllAccount(isFirstLoad) {
        //     if (this.tab === 0 && this.briefAccounts[0].length) {
        //         this.tableInputs.accountId =
        //             this.briefAccounts[0].find(c => c.id === this.employeeId)?.accountId;
        //             //  this.tableInputs.date = moment().format('YYYY-MM-DD')
        //             // this.accountIdFilter = !isFirstLoad ? this.briefAccounts[0].find(c => c.id === this.employeeId).accountId : null
        //         return;
        //     }
        //     if (this.allAccountsBrief.length) return;

        //     this.loading = true;
        //     return this.$store.dispatch('accounts/fetchAllBrief', {
        //         type: this.tab,
        //         regionIds: []
        //     }).then((data) => {
        //         this.briefAccounts[this.tab].push(...data);
        //         if (this.tab === 0) {
        //             this.tableInputs.accountId = this.currentUserData.accountId
        //             // this.tableInputs.date = moment().format('YYYY-MM-DD')
        //             // this.accountIdFilter = this.briefAccounts[0].find(c => c.id === this.employeeId).accountId
        //         }
        //     }).finally(() => {
        //         if (!isFirstLoad) this.loading = false;
        //     })
        // },

        getJournal(newOptionsScroll, isFirstLoad = false) {
            let page = 1;
            const { itemsPerPage } = this.scrollOptions;
            if (newOptionsScroll) page = newOptionsScroll.page || 1;

            if (this.needToResetShownRows) {
                this.$refs.table.resetShownRows();
                this.needToResetShownRows = false;
            }

            this.loading = true;
			return this.$store.dispatch('materialTransactions/fetchStatement', {
                // AccountId: this.accountIdFilter,
                AccountId: this.addFilterAccountEmployee ? this.accountIdFilter : null,
                Date: this.date,
                Page: page,
                PerPage: itemsPerPage || 50,
                type: this.$route.query.tab,
                // WithVisitTransaction: this.tab !== 0,
                // SelectOutAmount: this.tab === 0 ? true : null,
                SelectOutAmount: null,
                regionIds: []
            }).then(({ data, total }) => {
				const items = data.map(c => ({ 
                    ...c, 
                    in: c.in && c.in.amount, 
                    out: c.out && c.out.amount,
                    materialId: (c.in || c.out).materialId,
                    accountId: c.accountId,
                    notes: c.description,
                }));

                this.serverItemsLength = total;
                this.$refs.table.addScrollItems(items);
			}).finally(() => {
                if (!isFirstLoad) this.loading = false;
            })
		},

        moment,

        edit () {
            this.submitLoading = true
            this.loading = true
            this.$store.dispatch('materialTransactions/updateSingleTransaction', { 
                id: this.dataRow.id,
                materialId: this.materialId, 
                accountId: this.accountId, 
                out: this.quantityOut, 
                inAmount:  this.quantityIn, 
                notes: this.state === null || this.state === 0 ? this.notes : this.transactionState.find(c => c.id === this.state).name + ' ' + this.dataRow.notes,
                state: this.state,
                date: this.date,
                sellPrice: this.sellPrice ? this.sellPrice : 0,
                freeSample: Boolean(this.tableInputs.freeSample),
                // priceDiscount: this.
            }).then(() => {
                this.needToResetShownRows = true
                this.getJournal(null, false)
            }).finally(() => {
                this.submitLoading = false 
                // this.tableInputs.in = null
                // this.tableInputs.out = null
                this.$refs.table.resetInputsFocus()
                this.dialog = false
            })
        },

        addDate () {
            this.date = moment(this.date).add(1, 'day').format('YYYY-MM-DD');
            if (
                (moment(this.date).format('dddd') === 'Friday' && this.isDisabledFriday) || 
                (this.enableVacations && this.vacationsDayDetails.includes(this.date))
            ) {
                this.addDate()
            }
            return this.date
        },

        subDate() {
            this.date = moment(this.date).subtract(1, 'day').format('YYYY-MM-DD')
            if (
                (moment(this.date).format('dddd') === 'Friday' && this.isDisabledFriday) || 
                (this.enableVacations && this.vacationsDayDetails.includes(this.date))
            ) {
                this.subDate()
            }
            return this.date
        },

        getPriceValue () {
            this.disabledDiscount = false
            this.tableInputs.sellPrice = 0
            this.tableInputs.priceDiscount = null
            const material = this.materialBrief.find(c => c.id === this.tableInputs.materialId)
            if (material?.prices.length && material?.prices.find(c => c.priceId === this.tableInputs.priceType).price > 0) {
                this.tableInputs.sellPrice = material?.prices.find(v => v.priceId === this.tableInputs.priceType)?.price
            } else if (!material?.prices.length || material?.prices.find(c => c.priceId === this.tableInputs.priceType)?.price === 0 || (!material?.prices.find(c => c.priceId === this.tableInputs.priceType) || material?.prices.length < this.pricesTypes.length)) {
                this.dialogPriceType = true
            }
        },

        changeOnChangeMaterial () {
            this.disabledDiscount = false
            if (addPrice && enablePricesTypes) {
                this.tableInputs.priceType = null
                this.tableInputs.sellPrice = 0
                this.tableInputs.priceDiscount = 0
                this.tableInputs.payment = null
                if (this.materialBrief.find(c => c.id === this.tableInputs.materialId).prices.length === 0) {
                    // this.dialogPriceType = true
                    // this.allMaterials.find(c => c.id === this.tableInputs.materialId)
                    // this.tableInputs.sellPrice = this.materialBrief.find(c => c.id === this.tableInputs.materialId).materialPrice ? this.materialBrief.find(c => c.id === this.tableInputs.materialId).materialPrice : 0
                }
            }
        },

        resetInputsPrices () {
            if (this.addPrice && (!this.tableInputs.freeSample || this.tableInputs.freeSample === null)) {
                return [this.tableInputs.sellPrice, this.tableInputs.payment, this.tableInputs.priceDiscount]
            } else {
                this.tableInputs.sellPrice = 0
                this.tableInputs.payment = null
                this.tableInputs.priceDiscount = 0
            }

            if (this.enablePricesTypes) {
                this.tableInputs.priceType = null
            }
        },

        updatePrice () {
            const material = this.materialBrief.find(c => c.id === this.tableInputs.materialId)
            if (material.prices.length) {
                let priceFromApi = material?.prices.find(c => c.priceId === this.tableInputs.priceType).price

                priceFromApi = priceFromApi - (this.tableInputs.priceDiscount / 100) * priceFromApi
                
                this.tableInputs.sellPrice = priceFromApi
            } else {
                // fixed when add price to material mine endpoint and cancel fetchAll material endpoint from mounted hook
                const materialFromAllMaterial = this.materialBrief.find(c => c.id === this.tableInputs.materialId)
                this.tableInputs.sellPrice = materialFromAllMaterial.price - (this.tableInputs.priceDiscount / 100) * (materialFromAllMaterial.price)
            }
        },

        updateX () {
            if (this.materialBrief.find(c => c.id === this.tableInputs.materialId).prices.length !== 0) {
                this.disabledDiscount = this.tableInputs.sellPrice !== this.materialBrief.find(c => c.id === this.tableInputs.materialId).prices.find(c => c.priceId === this.tableInputs.priceType).price
            } else if (
                this.customPrice && 
                this.tableInputs.sellPrice !== this.materialBrief.find(c => c.id === this.tableInputs.materialId).materialPrice
            ) {
                this.disabledDiscount = true
            } else {
                this.disabledDiscount = false
            }
        },

        saveDate (date) {
            this.$refs.dateInputTable.save(date)
        },

        updateItemPrice () {
            this.submitLoading = true
            const pricesMaterial = this.materialBrief.find(c => c.id === this.tableInputs.materialId)?.prices
            if (pricesMaterial.find(c => c.priceId === this.tableInputs.priceType)?.price === 0) {
				pricesMaterial.find(c => c.priceId === this.tableInputs.priceType).price = this.price
				pricesMaterial.find(c => c.priceId === this.tableInputs.priceType).priceId = this.tableInputs.priceType
				pricesMaterial.find(c => c.priceId === this.tableInputs.priceType).priceDiscountRate = this.priceDiscountRate
			} 
            else if (!pricesMaterial.find(c => c.priceId === this.tableInputs.priceType)) {
				pricesMaterial.push({
					price: this.price,
					priceId: this.tableInputs.priceType,
					priceDiscountRate: this.priceDiscountRate
				})
			}
            // pricesMaterial.push({
            //     price: this.price,
            //     priceId: this.tableInputs.priceType,
            //     priceDiscountRate: this.priceDiscountRate
            // })
            this.$store.dispatch('materials/updatePrices', { 
                newPrices: pricesMaterial,
                id: this.tableInputs.materialId,
            }).then(() => {
                this.$store.dispatch('materials/fetchBrief', { withPrices: addPrice, employeeId: this.employeeId }).then(() => {
                    this.getPriceValue()
                })
                this.dialogPriceType = false
            }).finally(() => {
                this.submitLoading = false
            })
        }
    },

    mounted() {
        this.tab = +this.$route.query.tab || 0;
        this.$refs.table.resetInputs({
            accountId: this.employeeId,
            date: moment().format('YYYY-MM-DD')
        })
        this.loading = true;
        Promise.all([
            this.updateJournal(),
            // !this.materials.length ? this.$store.dispatch('materials/fetchAll', { withPrices: addPrice }) : null,
            this.enablePricesTypes ? this.$store.dispatch('pricesTypes/fetchAll') : null,

            !this.staff.length ? this.$requestStack.setRequest({
                key: 'accounts/fetchAllBrief/2',
                promise: this.$store.dispatch('accounts/fetchAllBrief', { type: 0, includeAddress: true, regionIds: [] }),
            }) : this.$requestStack.getRequest('accounts/fetchAllBrief/2').promise,

            !this.customers.length
                ? this.$requestStack.setRequest({
                    key: 'accounts/fetchAllBrief/1',
                    promise: this.$store.dispatch('accounts/fetchAllBrief', { type: 1, includeAddress: true, regionIds: [] })
                })
                : this.$requestStack.getRequest('accounts/fetchAllBrief/1').promise,

            this.$store.dispatch('materials/fetchBrief', { withPrices: addPrice, employeeId: this.employeeId }),

            enableVacations && !this.vacationsDayDetails.length ? this.$requestStack.setRequest({
                key: 'vacations/fetchAll',
                promise: this.$store.dispatch('vacations/fetchAll'),
            }) : this.$requestStack.getRequest('vacations/fetchAll').promise,

        ]).then(() => {
            this.$nextTick(() => {
				this.$refs.table.resetInputs()
			})
        }).finally(() => {
            this.tableInputs.accountId = this.currentUserData.accountId;
            this.tableInputs.date = this.moment().format('YYYY-MM-DD')
            // this.loading = false;
        })
    },

    metaInfo: {
        title: 'Samples journal',
        titleTemplate: '%s | Octopharma'
    }
}
</script>

<style scoped>
.paid-color-text {
    color: #f57f20
}
</style>
